import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import type { CountryProps } from '../../pages/Cannabis';

interface TimerPhase {
  label: string;
  desc: string;
  color: string;
  minuteStart: number;
  minuteEnd: number;
}

const PHASES: TimerPhase[] = [
  { label: 'Waiting', desc: 'Nothing yet — this is normal. Do NOT eat more.', color: '#60a5fa', minuteStart: 0, minuteEnd: 30 },
  { label: 'First Effects', desc: 'You may start feeling subtle warmth, tingling, or mood shift.', color: '#4ade80', minuteStart: 30, minuteEnd: 60 },
  { label: 'Coming Up', desc: 'Effects building. This is where most people feel it. Stay calm and enjoy.', color: '#d4a574', minuteStart: 60, minuteEnd: 90 },
  { label: 'Peak Zone', desc: 'Strongest effects. If comfortable, enjoy. If not, use the Panic Button tool.', color: '#f59e0b', minuteStart: 90, minuteEnd: 150 },
  { label: 'Plateau', desc: 'Effects leveling off. The intensity won\'t increase from here.', color: '#a78bfa', minuteStart: 150, minuteEnd: 240 },
  { label: 'Coming Down', desc: 'Gradually returning to baseline. May feel sleepy or hungry.', color: '#60a5fa', minuteStart: 240, minuteEnd: 480 },
];

function getPhase(minutes: number): TimerPhase {
  for (const p of PHASES) {
    if (minutes >= p.minuteStart && minutes < p.minuteEnd) return p;
  }
  return PHASES[PHASES.length - 1];
}

function formatTime(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function EdibleTimerTab(_props: CountryProps) {
  const [started, setStarted] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [dose, setDose] = useState(10);
  const [logged, setLogged] = useState<string[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Restore from localStorage
    const saved = localStorage.getItem('edibleTimer');
    if (saved) {
      const data = JSON.parse(saved);
      if (data.startTime) {
        setStartTime(data.startTime);
        setStarted(true);
        setDose(data.dose || 10);
        setLogged(data.logged || []);
      }
    }
  }, []);

  useEffect(() => {
    if (started && startTime) {
      intervalRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }
  }, [started, startTime]);

  function start() {
    const now = Date.now();
    setStartTime(now);
    setStarted(true);
    setElapsed(0);
    setLogged([]);
    localStorage.setItem('edibleTimer', JSON.stringify({ startTime: now, dose, logged: [] }));
  }

  function stop() {
    setStarted(false);
    setStartTime(null);
    setElapsed(0);
    setLogged([]);
    localStorage.removeItem('edibleTimer');
    if (intervalRef.current) clearInterval(intervalRef.current);
  }

  function logFeeling(feeling: string) {
    const mins = Math.floor(elapsed / 60);
    const entry = `${mins}min — ${feeling}`;
    const updated = [...logged, entry];
    setLogged(updated);
    if (startTime) localStorage.setItem('edibleTimer', JSON.stringify({ startTime, dose, logged: updated }));
  }

  const elapsedMinutes = Math.floor(elapsed / 60);
  const currentPhase = getPhase(elapsedMinutes);
  const progressPct = Math.min((elapsedMinutes / 480) * 100, 100);

  return (
    <div className="space-y-6 max-w-xl mx-auto">
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-white">Edible Onset Timer</h2>
        <p className="text-white/40 text-sm">Track your edible experience and know what to expect at each stage.</p>
      </div>

      {!started ? (
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <motion.div variants={staggerItem} className="space-y-2">
            <label className="text-white/60 text-sm">Dose taken (mg THC)</label>
            <div className="flex gap-2 flex-wrap">
              {[2.5, 5, 10, 15, 20, 25, 50].map(d => (
                <button key={d} onClick={() => setDose(d)} className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  dose === d ? 'bg-[#4ade80]/20 border-[#4ade80]/40 text-[#4ade80]' : 'bg-white/5 border-white/10 text-white/50 hover:border-white/20'
                }`}>{d}mg</button>
              ))}
            </div>
          </motion.div>

          <motion.div variants={staggerItem}>
            <button onClick={start} className="w-full py-4 rounded-2xl bg-[#4ade80]/20 border border-[#4ade80]/30 text-[#4ade80] font-bold text-lg transition-colors hover:bg-[#4ade80]/30">
              Start Timer
            </button>
          </motion.div>

          <motion.div variants={staggerItem} className="rounded-xl border border-white/5 bg-white/[0.02] p-4 space-y-2">
            <h4 className="text-white/60 text-xs font-semibold">Typical Timeline</h4>
            {PHASES.map(p => (
              <div key={p.label} className="flex items-baseline gap-3">
                <span className="text-[10px] font-mono w-20 shrink-0" style={{ color: p.color }}>{p.minuteStart}-{p.minuteEnd}min</span>
                <span className="text-white/40 text-xs">{p.label}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {/* Timer display */}
          <div className="text-center space-y-4 py-6">
            <p className="text-white/40 text-xs uppercase tracking-wider">Time since consumption</p>
            <p className="text-5xl font-bold text-white font-mono tracking-tight">{formatTime(elapsed)}</p>
            <div className="space-y-2">
              <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                <motion.div className="h-full rounded-full" style={{ backgroundColor: currentPhase.color, width: `${progressPct}%` }}
                  animate={{ width: `${progressPct}%` }} transition={{ duration: 1 }} />
              </div>
              <p className="text-sm font-semibold" style={{ color: currentPhase.color }}>{currentPhase.label}</p>
              <p className="text-white/50 text-xs max-w-sm mx-auto">{currentPhase.desc}</p>
            </div>
          </div>

          {/* Dose reminder */}
          <div className="rounded-xl border border-[#d4a574]/20 bg-[#d4a574]/5 p-3 text-center">
            <p className="text-[#d4a574] text-xs font-semibold">You took {dose}mg THC</p>
            {elapsedMinutes < 120 && (
              <p className="text-[#d4a574]/60 text-xs mt-1">Do NOT take more yet. Wait at least 2 hours before re-dosing.</p>
            )}
          </div>

          {/* Phase timeline */}
          <div className="space-y-1">
            {PHASES.map(p => {
              const active = currentPhase.label === p.label;
              const past = elapsedMinutes >= p.minuteEnd;
              return (
                <div key={p.label} className={`flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors ${active ? 'bg-white/5' : ''}`}>
                  <div className={`w-2 h-2 rounded-full shrink-0`} style={{ backgroundColor: past || active ? p.color : 'rgba(255,255,255,0.1)' }} />
                  <span className={`text-xs ${active ? 'text-white font-semibold' : past ? 'text-white/30' : 'text-white/20'}`}>{p.label}</span>
                  <span className="text-white/10 text-[10px] ml-auto">{p.minuteStart}–{p.minuteEnd}m</span>
                </div>
              );
            })}
          </div>

          {/* Log feelings */}
          <div className="space-y-3">
            <p className="text-white/40 text-xs font-semibold">How are you feeling?</p>
            <div className="flex gap-2 flex-wrap">
              {['Nothing yet', 'Slight buzz', 'Feeling good', 'Very high', 'Too high', 'Relaxed', 'Hungry', 'Sleepy', 'Anxious', 'Euphoric'].map(f => (
                <button key={f} onClick={() => logFeeling(f)}
                  className="px-2.5 py-1 text-[11px] rounded-lg border border-white/10 bg-white/[0.02] text-white/50 hover:border-white/20 hover:text-white/70 transition-colors">
                  {f}
                </button>
              ))}
            </div>
            {logged.length > 0 && (
              <div className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
                <p className="text-white/40 text-[10px] font-semibold">Your Log</p>
                {logged.map((entry, i) => <p key={i} className="text-white/50 text-xs">{entry}</p>)}
              </div>
            )}
          </div>

          {/* Stop */}
          <button onClick={stop} className="w-full py-3 rounded-xl border border-red-500/20 text-red-400/60 text-sm hover:bg-red-500/5 transition-colors">
            End Session
          </button>
        </div>
      )}
    </div>
  );
}
